import React, { RefObject } from 'react';
import {
  Box,
  keyframes,
  Tooltip,
  Typography,
  ClickAwayListener
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Scenario } from '../../types/attacks';
import { AttackStatus } from '../../types/operations';
import ControlPanel from './ControlPanel';
import RegionsPanel from './RegionsPanel';
import MetricsPanel from './MetricsPanel';
import OperationalErrorsPanel from './OperationalErrorsPanel';
import { Region } from '../../services/api';
import { EndingScenarioOverlay } from '../EndingScenarioOverlay';
import { useCanViewOperationalErrors, useOperationalErrors } from '../../hooks/useOperationalErrors';

interface AttackControlViewProps {
  scenario: Scenario;
  botCount: number;
  threadCount: number;
  botStatus: {
    pending: number;
    running: number;
    total: number;
  };
  attackStatus: AttackStatus;
  attackConfig: Record<string, any>;
  regions: Region[];
  selectedRegions: string[];
  regionRunningCounts?: Record<string, number>;
  regionCapacityCounts?: Record<string, number>;
  controlRef: RefObject<HTMLDivElement>;
  onBotCountChange: (count: number) => void;
  onThreadCountChange: (count: number) => void;
  onApplyCounts?: (botCount: number, threadCount: number) => void;
  onStart: () => void;
  onStartAttack?: (botCount: number, threadCount: number) => Promise<void>;
  onPause: () => void;
  onEnd: () => void;
  onEndScenario: () => void;
  onConfigUpdate: (config: Record<string, any>) => void;
  onApplyConfig?: (config: Record<string, any>) => Promise<void> | void;
  onRegionSelectionChange: (regions: string[]) => void;
  onShutdownRegions: (regions: string[]) => void;
  onTerminateRegions: (regions: string[]) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  endingScenario?: boolean;
  isApplyingCounts?: boolean;
  isRegionActionLoading?: boolean;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const AttackControlView: React.FC<AttackControlViewProps> = ({
  scenario,
  botCount,
  threadCount,
  botStatus,
  attackStatus,
  attackConfig,
  regions,
  selectedRegions,
  regionRunningCounts,
  regionCapacityCounts,
  controlRef,
  onBotCountChange,
  onThreadCountChange,
  onApplyCounts,
  onStart,
  onStartAttack,
  onPause,
  onEnd,
  onEndScenario,
  onConfigUpdate,
  onApplyConfig,
  onRegionSelectionChange,
  onShutdownRegions,
  onTerminateRegions,
  onDirtyChange,
  endingScenario = false,
  isApplyingCounts,
  isRegionActionLoading = false
}) => {
  const theme = useTheme();
  const MIN_PANEL_WIDTH = 420;
  const MAX_PANEL_WIDTH = 900;
  const DEFAULT_PANEL_WIDTH = 640;
  const MIN_NAV_CLEARANCE = 320;

  const getMaxPanelWidth = React.useCallback(() => {
    const safeMax = window.innerWidth - MIN_NAV_CLEARANCE;
    return Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, safeMax));
  }, []);

  const clampPanelWidth = React.useCallback(
    (width: number) => {
      const maxAllowed = getMaxPanelWidth();
      return Math.max(MIN_PANEL_WIDTH, Math.min(maxAllowed, width));
    },
    [getMaxPanelWidth]
  );

  const [activePanelOpen, setActivePanelOpen] = React.useState<'none' | 'regions' | 'metrics' | 'errors'>('none');
  const [panelWidth, setPanelWidth] = React.useState(() => clampPanelWidth(DEFAULT_PANEL_WIDTH));
  const [isResizing, setIsResizing] = React.useState(false);

  // Memoize scenario props to prevent unnecessary re-renders of MetricsPanel
  const scenarioTenantId = React.useMemo(() => scenario.tenant_id, [scenario.tenant_id]);
  const scenarioId = React.useMemo(() => scenario.id, [scenario.id]);
  const scenarioName = React.useMemo(() => scenario.name, [scenario.name]);

  // Operational errors (bot / transaction / execution) for the scenario in view. The feed polls
  // regardless of whether the panel is open so the rail tab can carry a live count, and stays
  // silent for roles without `view_operational_errors`.
  const canViewErrors = useCanViewOperationalErrors(scenarioTenantId);
  const errorsFeed = useOperationalErrors({
    tenantId: scenarioTenantId,
    scenarioId,
    enabled: canViewErrors,
  });
  const errorCount = errorsFeed.errors.length;

  // Never leave the rail stuck on a panel the current role cannot see.
  React.useEffect(() => {
    if (!canViewErrors) {
      setActivePanelOpen((current) => (current === 'errors' ? 'none' : current));
    }
  }, [canViewErrors]);

  // Handle panel resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const newWidth = window.innerWidth - e.clientX;
      // Constrain width between min and max
      const constrainedWidth = clampPanelWidth(newWidth);
      setPanelWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [clampPanelWidth, isResizing]);

  React.useEffect(() => {
    const handleResize = () => {
      setPanelWidth((current) => clampPanelWidth(current));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampPanelWidth]);

  const handleClickAway = React.useCallback(() => {
    if (activePanelOpen !== 'none' && !isResizing) {
      setActivePanelOpen('none');
    }
  }, [activePanelOpen, isResizing]);

  const toggleRegionsPanel = React.useCallback(() => {
    setActivePanelOpen(activePanelOpen === 'regions' ? 'none' : 'regions');
  }, [activePanelOpen]);

  const toggleMetricsPanel = React.useCallback(() => {
    setActivePanelOpen(activePanelOpen === 'metrics' ? 'none' : 'metrics');
  }, [activePanelOpen]);

  const toggleErrorsPanel = React.useCallback(() => {
    setActivePanelOpen(activePanelOpen === 'errors' ? 'none' : 'errors');
  }, [activePanelOpen]);

  return (
    <Box
      sx={{
        animation: `${fadeIn} 0.5s ease-out`,
        position: 'relative',
        mt: 4,
        px: 3,
      }}
    >
      {/* Loading Overlay for Ending Scenario */}
      <EndingScenarioOverlay open={endingScenario} />

      {/* Main Control Area - Full width, add right inset when panels open */}
      <Box
        sx={{
          width: '100%',
          transition: isResizing ? 'none' : 'padding-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pr: activePanelOpen !== 'none' ? `${Math.min(panelWidth * 0.5, 350)}px` : 0,
        }}
      >
        <ControlPanel
          ref={controlRef}
          scenario={scenario}
          botCount={botCount}
          threadCount={threadCount}
          botStatus={botStatus}
          attackStatus={attackStatus}
          attackConfig={attackConfig}
          onBotCountChange={onBotCountChange}
          onThreadCountChange={onThreadCountChange}
          onApplyCounts={onApplyCounts}
          isApplyingCounts={isApplyingCounts}
          onStart={onStart}
          onStartAttack={onStartAttack}
          onPause={onPause}
          onEnd={onEnd}
          onEndScenario={onEndScenario}
          onConfigUpdate={onConfigUpdate}
          onApplyConfig={onApplyConfig}
          onDirtyChange={onDirtyChange}
        />
      </Box>

      {/* Panel Toggle Buttons and Sliding Panels - wrapped in ClickAwayListener */}
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown">
        <Box sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: (theme) => theme.zIndex.drawer - 1 }}>
          {/* Panel Toggle Buttons - Fixed independently, always at panel left edge */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: activePanelOpen !== 'none' 
                ? `translateY(-50%) translateX(-${panelWidth}px)`
                : 'translateY(-50%) translateX(0)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              transition: isResizing ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: (theme) => theme.zIndex.drawer - 1,
              pointerEvents: 'auto',
            }}
          >
        <Tooltip title={activePanelOpen === 'regions' ? 'Close Regions' : 'Open Regions'} placement="left">
          <Box
            onClick={toggleRegionsPanel}
            sx={{
              backgroundColor: (theme) =>
                activePanelOpen === 'regions'
                  ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14)
                  : alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.92 : 0.96),
              border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              borderRight: 'none',
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
              p: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(12px)',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            <Typography
              sx={{
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                fontFamily: '"Space Mono", monospace',
                fontWeight: 600,
                  color: theme.palette.primary.main,
                fontSize: '0.85rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Regions & Bots
            </Typography>
          </Box>
        </Tooltip>

        <Tooltip title={activePanelOpen === 'metrics' ? 'Close Metrics' : 'Open Metrics'} placement="left">
          <Box
            onClick={toggleMetricsPanel}
            sx={{
              backgroundColor: (theme) =>
                activePanelOpen === 'metrics'
                  ? alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14)
                  : alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.92 : 0.96),
              border: `2px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
              borderRight: 'none',
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
              p: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(12px)',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                boxShadow: `0 0 20px ${alpha(theme.palette.secondary.main, 0.4)}`,
              },
            }}
          >
            <Typography
              sx={{
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                fontFamily: '"Space Mono", monospace',
                fontWeight: 600,
                  color: theme.palette.secondary.main,
                fontSize: '0.85rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Metrics
            </Typography>
          </Box>
        </Tooltip>

        {canViewErrors && (
          <Tooltip
            title={activePanelOpen === 'errors' ? 'Close Errors' : 'Open Errors'}
            placement="left"
          >
            <Box
              onClick={toggleErrorsPanel}
              data-testid="errors-panel-tab"
              sx={{
                backgroundColor: (theme) =>
                  activePanelOpen === 'errors'
                    ? alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.22 : 0.14)
                    : alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.92 : 0.96),
                border: `2px solid ${alpha(theme.palette.error.main, errorCount > 0 ? 0.7 : 0.4)}`,
                borderRight: 'none',
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(12px)',
                boxShadow: (theme) =>
                  errorCount > 0 ? `0 0 16px ${alpha(theme.palette.error.main, 0.35)}` : 'none',
                '&:hover': {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                  boxShadow: `0 0 20px ${alpha(theme.palette.error.main, 0.4)}`,
                },
              }}
            >
              <Typography
                sx={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  fontFamily: '"Space Mono", monospace',
                  fontWeight: 600,
                  color: theme.palette.error.main,
                  fontSize: '0.85rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                {errorCount > 0 ? `Errors (${errorCount})` : 'Errors'}
              </Typography>
            </Box>
          </Tooltip>
        )}
          </Box>

          {/* Sliding Right Panels */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 64, // Below TopStatusBar
              bottom: 0,
              width: `${panelWidth}px`,
              transform: activePanelOpen !== 'none' ? 'translateX(0)' : 'translateX(100%)',
              transition: isResizing ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: (theme) => theme.zIndex.drawer - 2,
              display: 'flex',
              flexDirection: 'row',
              backgroundColor: (theme) =>
                alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.98 : 0.97),
              backdropFilter: 'blur(20px)',
              borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              boxShadow: (theme) =>
                theme.palette.mode === 'dark' ? '-10px 0 40px rgba(0, 0, 0, 0.5)' : '-10px 0 32px rgba(2, 6, 23, 0.12)',
              pointerEvents: 'auto',
            }}
          >
        {/* Resize Handle */}
        {activePanelOpen !== 'none' && (
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              width: '6px',
              cursor: 'ew-resize',
              background: isResizing ? alpha(theme.palette.primary.main, 0.5) : 'transparent',
              borderRight: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              transition: 'background 0.2s ease',
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.3),
              },
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: '-10px',
                right: '-10px',
                top: 0,
                bottom: 0,
              },
            }}
          />
        )}

        {/* Panel Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 2,
          }}
        >
        {/* Regions Panel Content */}
        {activePanelOpen === 'regions' && (
          <RegionsPanel
            regions={regions}
            selectedRegions={selectedRegions}
            tenantId={scenarioTenantId}
            scenarioId={scenarioId}
            regionRunningCounts={regionRunningCounts}
            regionCapacityCounts={regionCapacityCounts}
            isCollapsed={false}
            onToggle={toggleRegionsPanel}
            onRegionSelectionChange={onRegionSelectionChange}
            onShutdownRegions={onShutdownRegions}
            onTerminateRegions={onTerminateRegions}
            isActionLoading={isRegionActionLoading}
            isAdvancedMode={true}
          />
        )}

        {/* Metrics Panel Content */}
        {activePanelOpen === 'metrics' && (
          <MetricsPanel
            isCollapsed={false}
            onToggle={toggleMetricsPanel}
            tenantId={scenarioTenantId}
            scenarioId={scenarioId}
            scenarioName={scenarioName}
            attackClass={scenario.attack_type?.attack_class}
          />
        )}

        {/* Operational Errors Panel Content */}
        {activePanelOpen === 'errors' && canViewErrors && (
          <OperationalErrorsPanel
            variant="card"
            isCollapsed={false}
            onToggle={toggleErrorsPanel}
            errors={errorsFeed.errors}
            loading={errorsFeed.loading}
            error={errorsFeed.error}
            degradedSources={errorsFeed.degradedSources}
            lastUpdatedMs={errorsFeed.lastUpdatedMs}
            onRefresh={errorsFeed.refresh}
          />
        )}
          </Box>
          </Box>
        </Box>
      </ClickAwayListener>
    </Box>
  );
};

export default AttackControlView;

